import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UserSubscription {
  plan: 'basic' | 'standard';
  status: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  roles: string[];
  subscription: UserSubscription | null;
  isPtaAdmin: boolean;
  isOrphaned: boolean;
  hasPta: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasPlan: (plan: 'basic' | 'standard') => boolean;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [isOrphaned, setIsOrphaned] = useState(false);
  const [hasPta, setHasPta] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const isMountedRef = useRef(true);

  const fetchUserData = useCallback(async (userId: string): Promise<{
    roles: string[];
    subscription: UserSubscription | null;
    isPtaAdmin: boolean;
    isOrphaned: boolean;
    hasPta: boolean;
  }> => {
    try {
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) throw rolesError;
      const fetchedRoles = rolesData?.map(r => r.role) || [];

      // Fetch the most-recent member row for this user. We use .limit(1) instead of
      // .single()/.maybeSingle() alone so that stray duplicate rows left behind by
      // abandoned onboarding attempts do not crash this query and silently flip the
      // user to "no PTA" (which would bounce them back to /onboarding).
      let { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('pta_id, is_pta_admin')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (memberError) throw memberError;

      // Fallback: if no row found by user_id, look up by email and link it.
      if (!memberData) {
        const userEmail = (await supabase.auth.getUser()).data.user?.email;
        if (userEmail) {
          const { data: byEmail } = await supabase
            .from('members')
            .select('id, pta_id, is_pta_admin')
            .eq('email', userEmail)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (byEmail?.pta_id) {
            await supabase
              .from('members')
              .update({ user_id: userId })
              .eq('id', byEmail.id);

            memberData = { pta_id: byEmail.pta_id, is_pta_admin: byEmail.is_pta_admin };
          }
        }
      }

      const fetchedIsPtaAdmin = memberData?.is_pta_admin || false;

      let fetchedSubscription: UserSubscription | null = null;
      if (memberData?.pta_id) {
        let { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('plan, status, expires_at')
          .eq('pta_id', memberData.pta_id)
          .in('status', ['active', 'trial'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subError) throw subError;

        if (!subData) {
          const { data: expiredSub } = await supabase
            .from('subscriptions')
            .select('plan, status, expires_at')
            .eq('pta_id', memberData.pta_id)
            .eq('status', 'trial_expired')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (expiredSub) {
            subData = expiredSub;
          }
        }

        if (subData?.expires_at && new Date(subData.expires_at) < new Date()) {
          await supabase
            .from('subscriptions')
            .update({ status: 'expired' })
            .eq('pta_id', memberData.pta_id)
            .eq('status', 'active');

          fetchedSubscription = null;
        } else {
          fetchedSubscription = subData;
        }
      }

      const fetchedIsOrphaned =
        !memberData &&
        fetchedRoles.length > 0 &&
        fetchedRoles.some((r) => r !== 'site_admin');

      return {
        roles: fetchedRoles,
        subscription: fetchedSubscription,
        isPtaAdmin: fetchedIsPtaAdmin,
        isOrphaned: fetchedIsOrphaned,
        hasPta: !!memberData?.pta_id,
      };
    } catch (error: any) {
      console.error('[AuthProvider] fetchUserData failed', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
        status: error?.status,
      });
      // Do not show a destructive toast — empty / incomplete-onboarding state is
      // a valid case and ProtectedRoute will route the user appropriately.
      return { roles: [], subscription: null, isPtaAdmin: false, isOrphaned: false, hasPta: false };
    }
  }, []);

  const applyUserData = useCallback((data: { roles: string[]; subscription: UserSubscription | null; isPtaAdmin: boolean; isOrphaned: boolean; hasPta: boolean }) => {
    if (!isMountedRef.current) return;
    setRoles(data.roles);
    setSubscription(data.subscription);
    setIsPtaAdmin(data.isPtaAdmin);
    setIsOrphaned(data.isOrphaned);
    setHasPta(data.hasPta);
  }, []);

  const refreshUserData = useCallback(async () => {
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) return;
    if (isMountedRef.current) {
      setUser(currentUser);
    }
    const data = await fetchUserData(currentUser.id);
    applyUserData(data);
  }, [fetchUserData, applyUserData]);

  useEffect(() => {
    isMountedRef.current = true;

    const clearUserData = () => {
      if (!isMountedRef.current) return;
      setRoles([]);
      setSubscription(null);
      setIsPtaAdmin(false);
      setIsOrphaned(false);
      setHasPta(false);
    };

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[AuthProvider] onAuthStateChange:', event, !!newSession?.user);

        if (!isMountedRef.current) return;

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          fetchUserData(newSession.user.id).then(applyUserData);
        } else {
          clearUserData();
        }
      }
    );

    const initializeAuth = async () => {
      console.log('[AuthProvider] Initializing auth...');
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();

        if (!isMountedRef.current) return;

        console.log('[AuthProvider] Initial session:', !!initialSession?.user);

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          const userData = await fetchUserData(initialSession.user.id);
          if (isMountedRef.current) {
            applyUserData(userData);
          }
        }
      } catch (error) {
        console.error('[AuthProvider] Error during initialization:', error);
      } finally {
        if (isMountedRef.current) {
          console.log('[AuthProvider] Initialization complete, setting loading=false');
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMountedRef.current = false;
      authSubscription.unsubscribe();
    };
  }, [fetchUserData, applyUserData]);


  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    let redirectUrl = `${window.location.origin}/pta-admin`;
    try {
      const ref = localStorage.getItem('pendingReferralCode');
      if (ref) redirectUrl += `?ref=${encodeURIComponent(ref)}`;
    } catch {}
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { full_name: fullName },
      }
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    return { data, error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) console.warn("Sign out warning:", error.message);
    } catch (e) {
      console.warn("Sign out exception:", e);
    }
    
    setUser(null);
    setSession(null);
    setRoles([]);
    setSubscription(null);
    setIsPtaAdmin(false);
    
    window.location.href = '/auth';
  };

  const hasRole = (role: string) => roles.includes(role);
  const hasPlan = (plan: 'basic' | 'standard') => {
    if (!subscription) return false;
    // Trial subscriptions grant full Standard plan access regardless of stored plan value
    const effectivePlan = subscription.status === 'trial' ? 'standard' : subscription.plan;
    return effectivePlan === plan || (plan === 'basic' && effectivePlan === 'standard');
  };

  return (
    <AuthContext.Provider value={{
      user, session, roles, subscription, isPtaAdmin, isOrphaned, hasPta, loading,
      signIn, signUp, signOut, hasRole, hasPlan, refreshUserData,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
